import React, { useState, useRef, useCallback, useEffect } from 'react';
import NeonProgress from '../ui/NeonProgress';
import { formatUploadBytes } from '../../lib/uploadVideo';

export interface UploadItem {
  id: string;
  file: File;
  customName: string;
  codec: 'h264' | 'h265';
  targetSizeMB?: number;
  width?: number;
  height?: number;
  normalizeAudio?: boolean;
  /** Dimensões originais (probe FE) */
  originalWidth?: number;
  originalHeight?: number;
  lockAspect?: boolean;
  status: 'configuring' | 'ready' | 'uploading' | 'error';
  errorMessage?: string;
  uploadLoaded?: number;
  uploadTotal?: number;
}

interface UploadLauncherProps {
  onItemsReady: (items: UploadItem[]) => void;
  /** Progresso externo enquanto App envia (id → loaded/total) */
  uploadProgress?: Record<string, { loaded: number; total: number }>;
  isUploading?: boolean;
}

/** Lê width/height via metadata do <video>. */
function probeVideoDimensions(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    const cleanup = () => {
      URL.revokeObjectURL(url);
      video.removeAttribute('src');
      video.load();
    };
    video.onloadedmetadata = () => {
      const width = video.videoWidth;
      const height = video.videoHeight;
      cleanup();
      if (width > 0 && height > 0) resolve({ width, height });
      else resolve(null);
    };
    video.onerror = () => {
      cleanup();
      resolve(null);
    };
    video.src = url;
  });
}

const UploadLauncher: React.FC<UploadLauncherProps> = ({
  onItemsReady,
  uploadProgress,
  isUploading = false,
}) => {
  const [items, setItems] = useState<UploadItem[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const generateId = () => Math.random().toString(36).substr(2, 9);

  const createUploadItem = (file: File): UploadItem => ({
    id: generateId(),
    file,
    customName: file.name.replace(/\.[^/.]+$/, ''),
    codec: 'h264',
    normalizeAudio: false,
    lockAspect: true,
    status: 'configuring',
  });

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const videoFiles = fileArray.filter(file =>
      file.type.startsWith('video/') ||
      /\.(mp4|avi|mov|mkv|wmv|flv|webm)$/i.test(file.name)
    );

    if (videoFiles.length === 0) {
      alert('Por favor, selecione apenas arquivos de vídeo.');
      return;
    }

    const newItems = videoFiles.map(createUploadItem);
    setItems(prev => [...prev, ...newItems]);

    // Probe dimensões em paralelo (UX imediata)
    for (const item of newItems) {
      const dims = await probeVideoDimensions(item.file);
      if (dims) {
        setItems(prev => prev.map(it =>
          it.id === item.id
            ? { ...it, originalWidth: dims.width, originalHeight: dims.height }
            : it
        ));
      }
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFiles(files);
    }
  }, [handleFiles]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
    e.target.value = '';
  }, [handleFiles]);

  const removeItem = (id: string) => {
    setItems(prev => prev.filter(item => item.id !== id));
  };

  const updateItem = (id: string, updates: Partial<UploadItem>) => {
    setItems(prev => prev.map(item =>
      item.id === id ? { ...item, ...updates } : item
    ));
  };

  /** Atualiza width/height com lock de proporção. */
  const updateDimension = (id: string, side: 'width' | 'height', raw: string) => {
    setItems(prev => prev.map(item => {
      if (item.id !== id) return item;
      const num = raw === '' ? undefined : parseInt(raw, 10);
      if (num !== undefined && (!Number.isFinite(num) || num < 1)) return item;

      const ow = item.originalWidth;
      const oh = item.originalHeight;
      const lock = item.lockAspect !== false;

      if (side === 'width') {
        if (lock && num != null && ow && oh) {
          return { ...item, width: num, height: Math.round((num * oh) / ow) };
        }
        return { ...item, width: num };
      }

      if (lock && num != null && ow && oh) {
        return { ...item, height: num, width: Math.round((num * ow) / oh) };
      }
      return { ...item, height: num };
    }));
  };

  const markItemReady = (id: string) => {
    setItems(prev => prev.map(item =>
      item.id === id ? { ...item, status: 'ready' as const } : item
    ));
  };

  const startUpload = () => {
    const readyItems = items.filter(item => item.status === 'ready');
    if (readyItems.length === 0) {
      alert('Configure pelo menos um vídeo antes de iniciar.');
      return;
    }
    onItemsReady(readyItems);
  };

  // Limpa lista quando upload externo termina (pai fecha modal)
  useEffect(() => {
    if (!isUploading && uploadProgress && Object.keys(uploadProgress).length === 0) {
      // no-op: pai controla fechamento
    }
  }, [isUploading, uploadProgress]);

  const formatFileSize = (bytes: number) => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  const readyCount = items.filter(item => item.status === 'ready').length;
  const uploadingCount = uploadProgress ? Object.keys(uploadProgress).length : 0;

  return (
    <div className="space-y-4">
      {/* Lista no topo */}
      {items.length > 0 && (
        <div className="space-y-3">
          <div className="flex justify-between items-center gap-2 flex-wrap">
            <h3 className="text-retro-pixel text-lg text-retro-primary">
              VÍDEOS SELECIONADOS ({items.length})
            </h3>

            {readyCount > 0 && !isUploading && (
              <button
                onClick={startUpload}
                className="retro-button retro-button-success px-6 py-3"
              >
                🚀 INICIAR COMPRESSÃO ({readyCount})
              </button>
            )}

            {isUploading && (
              <span className="text-sm text-retro-accent font-mono">
                Enviando {uploadingCount}/{items.length || uploadingCount}
              </span>
            )}
          </div>

          <div className="grid gap-3 max-h-[50vh] overflow-y-auto pr-1">
            {items.map(item => {
              const prog = uploadProgress?.[item.id];
              const loaded = prog?.loaded ?? item.uploadLoaded ?? 0;
              const total = prog?.total ?? item.uploadTotal ?? item.file.size;
              const pct = total > 0 ? Math.round((loaded / total) * 100) : 0;
              const isItemUploading = !!prog || item.status === 'uploading';

              return (
                <div key={item.id} className="retro-card p-3 border-2 border-retro-line">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-14 h-10 bg-retro-muted rounded border-2 border-retro-line flex items-center justify-center">
                      <span className="text-retro-pixel text-xs">📹</span>
                    </div>

                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex justify-between items-start gap-2">
                        <div className="min-w-0">
                          <h4 className="text-retro-text font-medium truncate text-sm">
                            {item.file.name}
                          </h4>
                          <p className="text-xs text-retro-muted">
                            {formatFileSize(item.file.size)}
                            {item.originalWidth && item.originalHeight && (
                              <span className="ml-2">
                                · orig. {item.originalWidth}×{item.originalHeight}
                              </span>
                            )}
                          </p>
                        </div>

                        {!isUploading && (
                          <button
                            onClick={() => removeItem(item.id)}
                            className="retro-button retro-button-danger px-2 py-1 text-sm"
                            title="Remover"
                            type="button"
                          >
                            ✕
                          </button>
                        )}
                      </div>

                      {item.status === 'error' && (
                        <div className="text-sm text-retro-danger">
                          ❌ {item.errorMessage}
                        </div>
                      )}

                      {isItemUploading && (
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs text-retro-muted font-mono">
                            <span>Enviando…</span>
                            <span>
                              {formatUploadBytes(loaded)} / {formatUploadBytes(total)} ({pct}%)
                            </span>
                          </div>
                          <NeonProgress
                            value={pct}
                            variant="upload"
                            label={`Upload de ${item.customName}`}
                            showPercentage={false}
                            animated
                          />
                        </div>
                      )}

                      {!isItemUploading && (item.status === 'configuring' || item.status === 'ready') && (
                        <div className="space-y-2">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            <div>
                              <label className="block text-xs text-retro-muted mb-1">
                                NOME
                              </label>
                              <input
                                type="text"
                                value={item.customName}
                                onChange={(e) => updateItem(item.id, { customName: e.target.value })}
                                className="retro-input w-full px-2 py-1.5 text-sm"
                                placeholder="Nome do arquivo"
                                disabled={item.status === 'ready'}
                              />
                            </div>

                            <div>
                              <label className="block text-xs text-retro-muted mb-1">
                                CODEC
                              </label>
                              <select
                                value={item.codec}
                                onChange={(e) => updateItem(item.id, { codec: e.target.value as 'h264' | 'h265' })}
                                className="retro-input w-full px-2 py-1.5 text-sm"
                                disabled={item.status === 'ready'}
                              >
                                <option value="h264">H.264 (Compatível)</option>
                                <option value="h265">H.265 (Eficiente)</option>
                              </select>
                            </div>

                            <div>
                              <label className="block text-xs text-retro-muted mb-1">
                                TAMANHO ALVO (MB)
                              </label>
                              <input
                                type="number"
                                min="1"
                                max="1000"
                                value={item.targetSizeMB || ''}
                                onChange={(e) => updateItem(item.id, {
                                  targetSizeMB: e.target.value ? parseInt(e.target.value) : undefined
                                })}
                                className="retro-input w-full px-2 py-1.5 text-sm"
                                placeholder="Opcional"
                                disabled={item.status === 'ready'}
                              />
                            </div>
                          </div>

                          {/* Resolução */}
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <label className="text-xs text-retro-muted">
                                RESOLUÇÃO (px)
                              </label>
                              <label className="flex items-center gap-1.5 text-xs text-retro-muted cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={item.lockAspect !== false}
                                  onChange={(e) => updateItem(item.id, { lockAspect: e.target.checked })}
                                  disabled={item.status === 'ready'}
                                />
                                Manter proporção
                              </label>
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="16"
                                max="7680"
                                value={item.width ?? ''}
                                onChange={(e) => updateDimension(item.id, 'width', e.target.value)}
                                className="retro-input w-full px-2 py-1.5 text-sm"
                                placeholder={item.originalWidth ? String(item.originalWidth) : 'Largura'}
                                disabled={item.status === 'ready'}
                              />
                              <span className="text-retro-muted text-sm">×</span>
                              <input
                                type="number"
                                min="16"
                                max="7680"
                                value={item.height ?? ''}
                                onChange={(e) => updateDimension(item.id, 'height', e.target.value)}
                                className="retro-input w-full px-2 py-1.5 text-sm"
                                placeholder={item.originalHeight ? String(item.originalHeight) : 'Altura'}
                                disabled={item.status === 'ready'}
                              />
                            </div>
                            <p className="text-xs text-retro-muted mt-0.5">
                              Vazio = resolução original
                              {item.originalWidth && item.originalHeight
                                ? ` (${item.originalWidth}×${item.originalHeight})`
                                : ''}
                            </p>
                          </div>

                          {/* Normalizar volume */}
                          <label className="flex items-start gap-2 cursor-pointer retro-card p-2 border border-retro-line-soft">
                            <input
                              type="checkbox"
                              className="mt-0.5"
                              checked={!!item.normalizeAudio}
                              onChange={(e) => updateItem(item.id, { normalizeAudio: e.target.checked })}
                              disabled={item.status === 'ready'}
                            />
                            <span>
                              <span className="text-sm text-retro-text block">Normalizar volume</span>
                              <span className="text-xs text-retro-muted">
                                Deixar perto de 0 dB sem estourar
                              </span>
                            </span>
                          </label>
                        </div>
                      )}

                      {item.status === 'configuring' && item.customName.trim() && !isUploading && (
                        <div className="flex justify-end">
                          <button
                            onClick={() => markItemReady(item.id)}
                            className="retro-button retro-button-success px-4 py-1.5 text-sm"
                            type="button"
                          >
                            ✓ PRONTO
                          </button>
                        </div>
                      )}

                      {item.status === 'ready' && !isItemUploading && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-retro-success">✓ Pronto para compressão</span>
                          {!isUploading && (
                            <button
                              onClick={() => updateItem(item.id, { status: 'configuring' })}
                              className="retro-button retro-button-secondary px-3 py-1.5 text-sm"
                              type="button"
                            >
                              📝 EDITAR
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Dropzone compacto embaixo */}
      {!isUploading && (
        <div
          className={`
            retro-card p-4 border-2 border-dashed transition-all duration-300
            ${isDragOver
              ? 'border-retro-primary bg-retro-primary bg-opacity-10'
              : 'border-retro-muted hover:border-retro-accent'
            }
          `}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="text-center">
            <h3 className="text-retro-pixel text-sm text-retro-primary mb-1">
              ADICIONAR VÍDEOS
            </h3>
            <p className="text-retro-muted text-xs mb-3">
              Arraste aqui ou selecione arquivos
            </p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="retro-button retro-button-primary px-4 py-2 text-sm"
              type="button"
            >
              📂 SELECIONAR
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="video/*,.mp4,.avi,.mov,.mkv,.wmv,.flv,.webm"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadLauncher;
